import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, FieldArray, formValueSelector, reduxForm, propTypes as formPropTypes } from 'redux-form';
import compose from 'lodash/flowRight';
import Icon from './common/icons/Icon';
import ReduxFormInput from './common/forms/ReduxFormInput';
import normalizeCreditCardNumber from './common/forms/normalizers/creditCardNumber';
import normalizeCreditCardCVC from './common/forms/normalizers/creditCardCVC';
import Loading from './common/components/Loading';

const renderRecipients = ({ fields, meta: { error, submitFailed } }) => (
  <div className="gift-subs--recipients__wrapper">
    <div>{submitFailed && error && <span>{error}</span>}</div>
    <div className="gift-subs--recipients-inputs">
      {fields.map((member, index) => (
        <div className="gift-subs--recipients-input" key={index}>
          <Field name={`${member}.emailAddress`} component={ReduxFormInput}>
            <input type="text" placeholder="Email Address"/>
          </Field>
          <div className="gift-subs--recipients-input__actions">
            <div className="to-recipient-check">
              <Field
                id={`${member}.emailCodeToRecipient`}
                name={`${member}.emailCodeToRecipient`}
                type="checkbox"
                component="input"
              />
              <label htmlFor={`${member}.emailCodeToRecipient`}>Email gift code directly to recipient</label>
            </div>
            {fields.length > 1 ? (
              <span className="gift-subs--recipients__delete" onClick={() => fields.remove(index)}>
                <Icon icon="Trash" />Remove
              </span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
    <button
      className="button button--icon button--theme--outline button--full-width"
      type="button"
      onClick={() => fields.push({})}
      disabled={fields.length >= 10}
    >
      {fields.length < 10 && <Icon icon="Plus" />}
      {fields.length < 10 ? 'Add Another Recipient' : 'Maximum purchase of 10 Gift Subscriptions'}
    </button>
  </div>
);

class GiftSubscriptionForm extends Component {
  static propTypes = {
    ...formPropTypes,
    price: PropTypes.number,
  };

  render() {
    const { error, submitting, handleSubmit, numRecipients, onSubmit, price } = this.props;
    const currentYear = new Date().getFullYear();
    const validYears = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(offset => offset + currentYear);

    if (!price) {
      return <Loading />;
    }

    const displayPrice = `$${(price * numRecipients / 100).toFixed(2)}`;

    return (
      <div className="subscribe__form" width="350">
        <div className="credit-card-form">
          <div className="card-wrapper">
          <div className="checkout-bar">
            <span className="checkout-bar__label">Total</span>
            <span className="checkout-bar__value">
              <span className="checkout-bar__total-value">{displayPrice}</span>
            </span>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="gift-subs--form-container">
              <h3>Enter the email address of the recipient(s).</h3>
              <p>
                You will receive a one-time use code with instructions on how to deliver your gift to its recipient.
              </p>
              <FieldArray name="recipients" component={renderRecipients} />
            </div>
            <h3>Enter your payment information</h3>
            <Field name="name" component={ReduxFormInput}>
              <input type="text" placeholder="Name"/>
            </Field>
            <Field name="email" component={ReduxFormInput}>
              <input type="text" placeholder="Email Address"/>
            </Field>
            <Field name="email_confirm" component={ReduxFormInput}>
              <input type="text" placeholder="Confirm Email Address"/>
            </Field>
            <Field name="number" component={ReduxFormInput} normalize={normalizeCreditCardNumber}>
              <input type="text" placeholder="Credit Card" />
            </Field>
            <div className="credit-card-form__card-details">
              <Field name="exp_month" component={ReduxFormInput}>
                <select>
                  <option value="">Month</option>
                  <option value="1">1 - January</option>
                  <option value="2">2 - February</option>
                  <option value="3">3 - March</option>
                  <option value="4">4 - April</option>
                  <option value="5">5 - May</option>
                  <option value="6">6 - June</option>
                  <option value="7">7 - July</option>
                  <option value="8">8 - August</option>
                  <option value="9">9 - September</option>
                  <option value="10">10 - October</option>
                  <option value="11">11 - November</option>
                  <option value="12">12 - December</option>
                </select>
              </Field>
              <Field name="exp_year" component={ReduxFormInput}>
                <select>
                  <option value="" key="year">
                    Year
                  </option>
                  {validYears.map(year => (
                    <option value={year} key={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </Field>
              <Field name="cvc" component={ReduxFormInput} normalize={normalizeCreditCardCVC}>
                <input type="text" placeholder="CVC" />
              </Field>
            </div>
            <Field name="postal_code" component={ReduxFormInput}>
              <input type="text" placeholder="Postal Code" />
            </Field>
            {error && <div className="input-validation">{error}</div>}
            <div>
              <button type="submit" className="button button--large" disabled={submitting}>
                Purchase Gift Subscriptions
              </button>
            </div>
          </form>
          <div className="price-confirmation">
            By selecting Purchase Gift Subscription, your card will be charged {displayPrice}.
          </div>
        </div>
        </div>
      </div>
    );
  }
}

const validate = values => {
  const errors = {};
  if (!values.recipients.length) {
    errors.recipients = 'Must have at least one recipient';
  } else {
    const recipientsErrors = [];

    values.recipients.forEach((recipient, i) => {
      const recipientErrors = {};

      if (!recipient.emailAddress) {
        recipientErrors.emailAddress = 'Email is required';
      }

      recipientsErrors[i] = recipientErrors;
    });

    if (recipientsErrors.length) {
      errors.recipients = recipientsErrors;
    }
  }
  if (!values.name || !values.name.trim()) {
    errors.name = 'Name is required.';
  }
  if (!values.email || !values.email.trim()) {
    errors.email = 'Email is required.';
  }
  if (!values.email_confirm) {
    errors.email_confirm = 'Please confirm your email address.';
  } else if (values.email_confirm !== values.email) {
    errors.email_confirm = 'Email addresses must match.';
  }
  if (!values.number) {
    errors.number = 'Credit card is required.';
  }
  if (!values.exp_month) {
    errors.exp_month = 'Month is required.';
  }
  if (!values.exp_year) {
    errors.exp_year = 'Year is required.';
  }
  if (!values.cvc) {
    errors.cvc = 'CVC is required.';
  }
  if (!values.postal_code) {
    errors.postal_code = 'Postal Code is required.';
  }
  return errors;
};

export default compose(
  reduxForm({ form: 'GiftSubscriptionForm', initialValues: { recipients: [{}] }, validate }),
  connect(state => {
    const selector = formValueSelector('GiftSubscriptionForm');
    return {
      numRecipients: selector(state, 'recipients').length,
    };
  })
)(GiftSubscriptionForm);



// WEBPACK FOOTER //
// ./src/GiftSubscriptionForm.js